import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ServicePageData for the "Data Quality and Enhancement" page (route: /data-quality/).
 *
 * This is a like-for-like migration of the previous hand-built DataQualityPage.jsx:
 * every paragraph below is the original copy, unchanged. Two structural adjustments
 * were made deliberately (see inline notes) purely to satisfy the shared layout's
 * accessibility requirements — no wording, claims, or links were added or altered.
 *
 * @type {import('../../components/service/ServicePageLayout').ServicePageData}
 */
const dataQualityPageData = {
  slug: 'data-quality',
  title: 'A quick guide on data quality [why is it important?]',
  // Reuses the existing meta description verbatim as the hero's visible intro line —
  // previously this copy only lived in a <meta> tag and wasn't shown on the page.
  introduction:
    'Learn about data quality importance, factors affecting data quality including accuracy, completeness, and validity. Discover how quality data improves business decisions and ROI.',
  hero: {
    image: '/images/data-quality-why-is-it-important.webp',
  },
  sections: [
    {
      // NOTE: the original page had no <h2> elements at all, only bold paragraphs
      // standing in for headings. Splitting the prose into two titled sections gives
      // the page a proper heading hierarchy (h1 > h2) without changing any wording.
      id: 'what-is-data-quality',
      title: 'What is data quality?',
      content: [
        "So, first things first. What is data quality? In it's simplest form data quality is the measure of the condition of your data within a CRM, based on consistency, completeness, validity, relevancy, timeliness and whether it's accurate and up to date. By doing this it makes sure quality levels are being measured frequently and can therefore help organizations identify any errors that need to be resolved quickly and run checks to see whether the data is fit to serve its intended purpose.",
        <p key="find-out-more" className="text-xl font-semibold text-gray-900 mb-4">
          Data is a hot topic right now.{' '}
          <Link to="/product" className="text-pink-600 hover:text-pink-700 underline" onClick={() => window.scrollTo(0, 0)}>
            Click here
          </Link>{' '}
          to find out about our market leading data quality tool.
        </p>,
        "All marketing agencies want to talk about data insights and the value that they can derive from this data. There's a good reason for that. In modern times data is one of the most valuable resources available for marketers, agencies, publishers, media companies and more.",
        <p key="ibm-stat" className="text-gray-700 leading-relaxed mb-6 text-lg">
          Data is only ever data, unless it's high quality, accurate and brought to life in the right way. IBM
          estimates that bad data costs the U.S. economy{' '}
          <a
            href="https://intelligent-ds.com/blog/the-real-cost-of-bad-data#:~:text=IBM%20has%20estimated%20that%20bad,12%25%20of%20its%20total%20revenue."
            className="text-pink-600 hover:text-pink-700 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            $3.1 trillion per year.
          </a>{' '}
          Those costs come from the salaries businesses must spend correcting bad data and errors that cause
          mistakes with customers as well as loss of revenue from loyal customers.
        </p>,
        'Improving the quality of your data is a big opportunity.',
        'Factors which contribute to the quality of data broken down:',
      ],
      // NOTE: the six "Accuracy / Completeness / ..." paragraphs are expressed here as
      // a semantic list (matching ServiceContentSection's `list` support) instead of six
      // separate paragraphs — same copy, more structured markup.
      list: [
        {
          label: 'Accuracy',
          text:
            'Accuracy refers to how well the data describes the real-world conditions it aims to describe. Inaccurate data creates clear problems when it comes to decision making, marketing campaigns and relationship building with customers.',
        },
        {
          label: 'Completeness',
          text:
            "If data is complete, there are no gaps in it. Everything that was supposed to be collected was successfully collected. Gaps in data can lead to customers not being contacted or the wrong customer being contacted. If your data is incomplete, you might have trouble gathering accurate insights from it and therefore make wrong decisions.",
        },
        {
          label: 'Relevancy',
          text:
            "The data you collect should also be useful for the campaigns and initiatives you plan to use it for. There is no point in having all of this data if it's not relevant to your goals.",
        },
        {
          label: 'Validity',
          text:
            'This refers to how the data is collected, as data is only ever valid if it is in the right format. If your business data does not meet this criteria, you will have trouble using it for any business function.',
        },
        {
          label: 'Timeliness',
          text:
            'Data typically becomes less quality and less accurate as it gets older. The best way to prevent this is to use data that reflects the current reality.',
        },
        {
          label: 'Consistency',
          text:
            "Data should be consistent in its content and format. If it isn't consistent, groups may be operating under assumptions and therefore your different business functions may not be well coordinated.",
        },
      ],
    },
    {
      id: 'why-it-matters',
      title: 'Why is data quality management important?',
      content: [
        "Many businesses nowadays are using data more and more to make decisions across the company. As more people reap the benefits good data quality control and increase their businesses ROI using it, in industries it is now becoming a matter of keeping up with the competition. Companies that don't have data quality assurance are at risk losing the race and falling behind their competition.",
        "The better your data's quality, the more you can get out of it.",
        'Many of today\'s businesses integrate data quality tools into all of their integral strategies. This increased integration means that data quality can impact many aspects of a business flow.',
        "Data quality issues are also a big no no for compliance-related issues. It's harder to demonstrate industry compliance if your data is not well maintained, this is vital for sensitive personal data.",
        'Good data management is crucial for keeping up with your competition and taking advantage of big opportunities. High-quality data can provide various benefits including improving decision making so budget is spent wisely, better audience targeting and more effective campaigns, relationships with customers are improved, and your business will have an advantage over competitors.',
        <p key="get-in-touch" className="text-gray-700 leading-relaxed mb-6 text-lg">
          To find out how Lloyd James could help you improve the quality of your data{' '}
          <Link to="/contact" className="text-pink-600 hover:text-pink-700 underline" onClick={() => window.scrollTo(0, 0)}>
            click here to get in touch.
          </Link>
        </p>,
      ],
    },
  ],
  // No stats, benefits, or pricing data exists for this page — omitted rather than
  // invented, which also exercises ServicePageLayout's "optional section" behaviour.
  pricing: null,
  enquiryForm: {
    enabled: true,
    serviceName: 'Data Quality and Enhancement',
    heading: 'Enquire now',
    description: 'Get in touch to discuss how Lloyd James can help improve the quality of your data.',
  },
  seo: {
    // Preserves the exact metadata the page already had.
    title: 'Data Quality Guide - Why Is It Important? - Lloyd James Data Consultancy',
    description:
      'Learn about data quality importance, factors affecting data quality including accuracy, completeness, and validity. Discover how quality data improves business decisions and ROI.',
    keywords: 'data quality, data accuracy, data completeness, data management, CRM data quality, Lloyd James, data quality tools',
    canonical: '/data-quality',
    type: 'article',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      // The previous <ArticleSchema headline="..." /> call passed a prop named
      // "headline" into a component that only reads "title", so the rendered
      // JSON-LD's headline was silently `undefined` in production. Building the
      // object directly here fixes that.
      headline: 'A quick guide on data quality [why is it important?]',
      description:
        "Data quality is the measure of the condition of your data within a CRM, based on consistency, completeness, validity, relevancy, timeliness and whether it's accurate and up to date.",
      author: {
        '@type': 'Person',
        name: 'Lloyd James Data Consultancy',
      },
      datePublished: '2024-01-01',
      dateModified: '2024-01-01',
      image: '/images/data-quality-why-is-it-important.webp',
    },
  },
};

export default dataQualityPageData;
